import { FeedItem } from '@/components/feed/useFeedStore';
import {
    BookingState,
    PendingDataProps,
    TradingData,
} from '@/store/trade-store';
import dayjs from 'dayjs';

interface PairInfoRaw {
    pairIndex: string;
    symbol: string;
    openInterestLong: string;
    openInterestShort: string;
    maxOpenInterest: string;
    fundingRateLong: string;
    fundingRateShort: string;
    rolloverRate: string;
    maxLeverage: string;
    spread: string;
    onePercentDepthAbove: string;
    onePercentDepthBelow: string;
}

interface TradeRawData {
    trader: string;
    pairIndex: number;
    index: number;
    positionSize: string;
    buy: boolean;
    leverage: string;
    tp: string;
    sl: string;
    openPrice: string;
    minPrice: string;
    maxPrice: string;
    type: string;
    isLimitOrder: boolean;
    executionPrice: string;
    initialPosToken: string;
    collateralBeforeFee: string;
    createdAt: string;
    aSpreadP: string;
}

interface RawPendingData {
    blockNumber: string;
    buy: boolean;
    kind: number;
    leverage: string;
    limitOrderType: number;
    nftHolder: string;
    nftId: string;
    openPrice: string;
    orderId: string;
    pairIndex: string;
    positionSizeUsdc: string;
    sl: string;
    slippageP: string;
    spreadReductionP: string;
    tokenId: string;
    tp: string;
    tradeIndex: string;
    trader: string;
    wantedPrice: string;
}

type FeedEvents = {
    connected: void;
    disconnected: void;
    error: Event;
    pair_params: BookingState[];
    trades: TradingData[];
    feed: any;
    pending_orders: PendingDataProps[];
};

type EventKey = keyof FeedEvents;

type Listener<K extends EventKey> = (payload: FeedEvents[K]) => void;

const MAX_RETRY = 5;
const RETRY_BASE_DELAY_MS = 2000;

export class FeedSSE {
    private eventSource?: EventSource;

    private static instance: FeedSSE;
    trades: TradingData[] = [];
    pair_params: BookingState[] = [];
    feed: FeedItem[] = [];
    pending_orders: PendingDataProps[] = [];
    /**
     * Event listeners
     */
    private listeners: {
        [K in EventKey]?: Set<Listener<K>>;
    } = {};

    address?: string;
    private retryCount = 0;
    private retryTimer?: ReturnType<typeof setTimeout>;
    private manualClose = false;

    private constructor() {}

    init() {
        this.manualClose = false;
        this.eventSource = new EventSource(
            `${import.meta.env.VITE_SSE_TRADE}/${this.address}/legacy`
        );

        this.eventSource.onopen = () => {
            console.log('SSE connected');
            this.retryCount = 0;

            this.emit('connected', undefined);
            this.emit('pair_params', this.pair_params);
            this.emit('trades', this.trades);
            this.emit('feed', this.feed);
            // this.emit('pending_orders', this.pending_orders);
        };

        this.eventSource.addEventListener('pair_params', (event: Event) => {
            const messageEvent = event as MessageEvent;

            const data = JSON.parse(messageEvent.data) as PairInfoRaw[];

            this.pair_params = this.mergePairInfo(data);

            this.emit('pair_params', this.pair_params);
        });

        this.eventSource.addEventListener('trades', (event: Event) => {
            const messageEvent = event as MessageEvent;

            const trades = JSON.parse(messageEvent.data) as TradeRawData[];
            this.trades = this.mergeTradeData(trades);

            this.emit('trades', this.trades);
        });

        this.eventSource.addEventListener('feed', (event: Event) => {
            const messageEvent = event as MessageEvent;

            this.feed = JSON.parse(messageEvent.data);

            this.emit('feed', this.feed);
        });

        this.eventSource.addEventListener('pending_orders', (event: Event) => {
            const messageEvent = event as MessageEvent;

            const pending_orders = JSON.parse(messageEvent.data);
            this.pending_orders = this.mergePendingOrder(pending_orders);

            // this.emit('pending_orders', this.pending_orders);
        });

        this.eventSource.onerror = (error) => {
            console.error('SSE error', error);

            this.emit('error', error);

            if (this.eventSource?.readyState === EventSource.CLOSED) {
                this.emit('disconnected', undefined);
                this.scheduleReconnect();
            }
        };
    }

    private scheduleReconnect() {
        if (this.manualClose || this.retryCount >= MAX_RETRY) {
            if (this.retryCount >= MAX_RETRY) {
                console.warn(
                    `SSE max retries (${MAX_RETRY}) reached. Giving up.`
                );
            }
            return;
        }

        this.retryCount++;
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, this.retryCount - 1);
        console.log(
            `SSE reconnecting in ${delay}ms (attempt ${this.retryCount}/${MAX_RETRY})...`
        );

        this.retryTimer = setTimeout(() => {
            this.eventSource?.close();
            this.init();
        }, delay);
    }

    static getInstance(): FeedSSE {
        if (!FeedSSE.instance) {
            FeedSSE.instance = new FeedSSE();
        }

        return FeedSSE.instance;
    }

    setAddress(address: string) {
        this.address = address;
        this.retryCount = 0;
        clearTimeout(this.retryTimer);
        if (this.eventSource) this.eventSource.close();
        this.init();
    }

    getAddress() {
        return this.address;
    }

    /**
     * Subscribe
     */
    on<K extends EventKey>(event: K, listener: Listener<K>) {
        if (!this.listeners[event]) {
            // Type assertion to appease TypeScript's strict indexed generic checks
            this.listeners[event] =
                new Set() as unknown as (typeof this.listeners)[typeof event];
        }

        this.listeners[event]!.add(listener);

        /**
         * unsubscribe function
         */
        return () => {
            this.off(event, listener);
        };
    }

    /**
     * Unsubscribe
     */
    off<K extends EventKey>(event: K, listener: Listener<K>) {
        this.listeners[event]?.delete(listener);
    }

    /**
     * Emit
     */
    private emit<K extends EventKey>(event: K, payload: FeedEvents[K]) {
        this.listeners[event]?.forEach((listener) => {
            listener(payload);
        });
    }

    private mergeTradeData(trades: TradeRawData[]): TradingData[] {
        return trades.map((input) => {
            const date =
                input.createdAt && input.createdAt.includes('1970')
                    ? dayjs().utc()
                    : dayjs(input.createdAt).utc();

            return {
                trade_info: {
                    trader: input.trader,
                    pair_index: input.pairIndex,
                    index: input.index,
                    initial_pos_token: Number(input.initialPosToken),
                    position_size_usd: Number(input.positionSize),
                    open_price: Number(input.openPrice),
                    buy: input.buy,
                    leverage: Number(input.leverage),
                    tp: Number(input.tp),
                    sl: Number(input.sl),
                },

                open_fee: 0,

                min_price: Number(input.minPrice),

                max_price: Number(input.maxPrice),

                block: 0,

                type: Number(input.type),

                spread: Number(input.aSpreadP),

                execution_price: Number(input.executionPrice || 0),

                collateral_before_fee: Number(input.collateralBeforeFee),

                created_at: {
                    seconds: Math.floor(date.valueOf() / 1000),
                    nanos: (date.valueOf() % 1000) * 1e6,
                },

                lastUpdatedDate: Date.now(),
            };
        });
    }

    private mergePairInfo(pairs: PairInfoRaw[]): BookingState[] {
        return pairs.map((raw) => {
            return {
                pair_index: Number(raw.pairIndex),
                symbol: raw.symbol,
                open_interest_long: raw.openInterestLong,
                open_interest_short: raw.openInterestShort,
                max_open_interest: raw.maxOpenInterest,
                funding_rate_long: raw.fundingRateLong,
                funding_rate_short: raw.fundingRateShort,
                rollover_rate: raw.rolloverRate,
                max_leverage: Number(raw.maxLeverage),
                spread: raw.spread,
                one_percent_depth_above: raw.onePercentDepthAbove,
                one_percent_depth_below: raw.onePercentDepthBelow,
                // Added missing fields expected by BookingState (use strings where store expects string)
                open_fee: '0',
                nft_limit_order_fee: '0',
            };
        });
    }

    //     [
    //     {
    //         "OrderId": "129713",
    //         "Kind": 0,
    //         "Trader": "0x7D2FBcd4932C7d99D825015FA28B7Eb7E39ab65D",
    //         "PairIndex": "0",
    //         "TradeIndex": "0",
    //         "PositionSizeUsdc": "0",
    //         "OpenPrice": "0",
    //         "Leverage": "0",
    //         "Tp": "0",
    //         "Sl": "0",
    //         "WantedPrice": "0",
    //         "SlippageP": "0",
    //         "SpreadReductionP": "0",
    //         "TokenId": "0",
    //         "BlockNumber": "30826925",
    //         "Buy": false,
    //         "NftHolder": "0x0000000000000000000000000000000000000000",
    //         "NftId": "0",
    //         "LimitOrderType": 0
    //     }
    // ]

    // [
    //     {
    //         "blockNumber": "30828174",
    //         "buy": false,
    //         "kind": 0,
    //         "leverage": "0",
    //         "limitOrderType": 0,
    //         "nftHolder": "0x0000000000000000000000000000000000000000",
    //         "nftId": "0",
    //         "openPrice": "0",
    //         "orderId": "129727",
    //         "pairIndex": "0",
    //         "positionSizeUsdc": "0",
    //         "sl": "0",
    //         "slippageP": "0",
    //         "spreadReductionP": "0",
    //         "tokenId": "0",
    //         "tp": "0",
    //         "tradeIndex": "0",
    //         "trader": "0x7D2FBcd4932C7d99D825015FA28B7Eb7E39ab65D",
    //         "wantedPrice": "0"
    //     }
    // ]

    private mergePendingOrder(pairs: RawPendingData[]): PendingDataProps[] {
        return pairs.map((raw) => {
            return {
                trade_info: {
                    trader: raw.trader,
                    pair_index: Number(raw.pairIndex),
                    index: Number(raw.tradeIndex),
                    initial_pos_token: Number(0),
                    position_size_usd: Number(raw.positionSizeUsdc),
                    open_price: Number(raw.openPrice),
                    buy: raw.buy,
                    leverage: Number(raw.leverage),
                    tp: Number(raw.tp),
                    sl: Number(raw.sl),
                },
                block: Number(raw.blockNumber),
                wanted_price: Number(raw.wantedPrice),
                slippage: Number(raw.slippageP),
                spread_reduction: Number(raw.spreadReductionP),
                token_id: Number(raw.tokenId),
                pending_id: Number(raw.orderId),
            };
        });
    }

    close() {
        this.manualClose = true;
        this.retryCount = 0;
        clearTimeout(this.retryTimer);

        if (this.eventSource?.readyState === 1) {
            this.eventSource?.close();

            this.emit('disconnected', undefined);
            Object.values(this.listeners)?.forEach((listener) => {
                listener.clear();
            });
        }
    }
}
